import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../lib/api';

// Notification preference types that support both emailDM and Slack
export interface NotificationPreference {
  type: 'emailDM' | 'slack' | null;
  slackChannel?: {
    id: string;
    name: string;
  };
}

export interface SlackChannel {
  id: string;
  name: string;
  isPrivate: boolean;
  isMember: boolean;
}

interface GetSlackChannelsResponse {
  data: {
    channels: SlackChannel[];
  };
}

// Unified notification preference response
export interface NotificationPreferences {
  spotlight?: NotificationPreference;
  productUpdate?: NotificationPreference;
}

interface GetNotificationPreferenceResponse {
  data: NotificationPreferences;
}

interface SaveNotificationPreferenceRequest {
  spotlight?: NotificationPreference;
  productUpdate?: NotificationPreference;
}

interface SaveNotificationPreferenceResponse {
  data: NotificationPreferences;
}

// Get notification preferences (unified endpoint for user and tenant)
export const useNotificationPreference = (enabled = true) => {
  return useQuery({
    queryKey: ['notificationPreference'],
    queryFn: async () => {
      const { data } = await api.get<GetNotificationPreferenceResponse>(
        '/api/core/pleri/notification-preference',
      );
      return data;
    },
    enabled,
  });
};

// Update notification preferences (unified endpoint for user and tenant)
export const useUpdateNotificationPreference = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SaveNotificationPreferenceRequest) => {
      const { data } = await api.post<SaveNotificationPreferenceResponse>(
        '/api/core/pleri/notification-preference',
        request,
      );
      await queryClient.refetchQueries({
        queryKey: ['notificationPreference'],
      });

      return data;
    },
  });
};

// Get Slack channels
export const useSlackChannels = (enabled = true) => {
  return useQuery({
    queryKey: ['slackChannels'],
    queryFn: async () => {
      const { data } = await api.get<GetSlackChannelsResponse>(
        '/api/core/pleri/slack-channels',
      );
      return data.data.channels.filter((channel) => channel.isMember);
    },
    enabled,
  });
};
